import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { formatStepName } from '../../../utils/helpers'
import { trpc } from '../../../utils/trpc'
import type { FormComponent, JumpToItem } from './types'

export const useCycleExecution = ({ taskId }: { taskId: string }) => {
	const { user } = sessionStore()
	const userId = user?.id || ''
	const [isPreviewMode, setIsPreviewMode] = React.useState(false)

	const {
		data: taskData,
		refetch,
		isLoading: isTaskLoading,
		isError: isTaskError,
		error: taskError,
	} = useQuery({
		...trpc.client.tasks.show.queryOptions({
			taskId,
			previewAsRole: isPreviewMode ? ('participant' as const) : undefined,
		}),
		enabled: !!taskId && !!userId,
	})

	// Query for rejection data - this is optional, don't treat errors as critical
	const { data: rejectionData, isLoading: isRejectionLoading } = useQuery({
		...trpc.client.tasks.getRejectionData.queryOptions({ taskId: taskId || '' }, { enabled: Boolean(taskId) }),
	})

	const cycleStepType = taskData?.cycleStepType || ''
	const cycleStepName =
		taskData?.cycleStepName || taskData?.taskName || (cycleStepType ? formatStepName(cycleStepType) : '-')
	const cycleStepData = {
		id: taskData?.cycleStepId || cycleStepType,
		name: cycleStepName,
	}

	const calculatedRole = taskData?.calculatedRole || 'none'

	const formComponents = taskData?.formComponents || []
	const conditionalSourceValues = (taskData?.conditionalSourceValues ?? {}) as Record<string, string>

	const collectJumpToItems = (components: FormComponent[], items: JumpToItem[] = []): JumpToItem[] => {
		components.forEach((component) => {
			if (component.name === 'container' || component.name === 'repeatable') {
				const customData = component.custom as Record<string, unknown>
				const label = typeof customData?.label === 'string' ? customData.label : ''

				if (label) {
					items.push({ id: component.id, label })
				}

				const children = component.children as FormComponent[] | undefined
				const shouldJumpToChildren = children?.length && component.name !== 'repeatable'
				if (shouldJumpToChildren) {
					collectJumpToItems(children, items)
				}
			}
		})

		return items
	}

	const jumpToItems = collectJumpToItems(formComponents)

	const canEdit = taskData?.accessLevel === 'write'
	const isLoading = isTaskLoading || isRejectionLoading
	// Only treat task data errors as critical, rejection data is optional
	const isError = isTaskError
	const technicalError = taskError ? taskError.message?.trim() || 'No additional details available' : undefined

	const coManagerResponseMode = taskData?.coManagerResponseMode

	return {
		cycle: { name: taskData?.evaluation.cycleName },
		cycleStep: cycleStepData,
		formComponents,
		conditionalSourceValues,
		isCycleAdmin: taskData?.isCycleAdmin, // Ariel
		aiSummaries: taskData?.aiSummaries || [],
		refetch,
		calculatedRole,
		stepAccessLevel: taskData?.accessLevel,
		formData: taskData || null,
		taskData: {
			...(taskData || null),
			rejection: rejectionData
				? {
						...rejectionData,
					}
				: null,
		},
		evaluationHeaderConfig: taskData?.evaluationHeaderConfig,
		jumpToItems,
		cycleStepActions: taskData?.cycleStepActions,
		requiresSignature: taskData?.requireSignature ?? false,
		allowDenial: taskData?.allowDenial ?? false,
		allowCAEdit: taskData?.allowCAEdit ?? false,
		canEdit,
		isLoading,
		isError,
		technicalError,
		isPreviewMode,
		setIsPreviewMode,
		coManagerResponseMode,
	}
}
